import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2 } from 'lucide-react';
import { getAuthHeader } from '../../services/tokenService';
import TypesDispositifs from './TypesDispositifs';
import { couleurType } from './couches';

const fr = (d) => (d ? String(d).slice(0, 10).split('-').reverse().join('/') : '—');

const Bloc = ({ titre, n, children, testid }) => (
  <div data-testid={testid}>
    <p className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-slate-400">{titre} · {n}</p>
    {n === 0 ? <p className="text-xs text-slate-500">Aucun</p> : <div className="max-h-56 space-y-1 overflow-auto pr-1">{children}</div>}
  </div>
);

export default function FicheTerritoire({ territoire, onFermer, publique = false, type = null }) {
  const [detailBrut, setDetail] = useState(null);
  const detail = detailBrut && type ? { ...detailBrut, contrats: detailBrut.contrats.filter((c) => c.dispositif === type), chantiers: detailBrut.chantiers.filter((c) => c.dispositif === type) } : detailBrut;
  const nom = territoire?.territoire;
  useEffect(() => {
    if (publique || !nom) return;
    setDetail(null);
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/carte-vivante/territoire/${encodeURIComponent(nom)}`, { headers: getAuthHeader() })
      .then((r) => setDetail(r.data)).catch(() => setDetail({ contrats: [], chantiers: [], dommages: [] }));
  }, [nom, publique]);
  if (!territoire) return null;
  return (
    <div className="fixed inset-y-0 right-0 z-[60] w-full max-w-md overflow-y-auto border-l border-white/10 bg-slate-950/95 p-5 text-slate-100 shadow-2xl backdrop-blur" data-testid="fiche-territoire">
      <div className="mb-4 flex items-start justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-emerald-300">Fiche territoire</p>
          <h3 className="text-2xl font-bold" data-testid="fiche-territoire-nom">{territoire.territoire}</h3>
        </div>
        <button type="button" onClick={onFermer} className="rounded-full p-1.5 hover:bg-white/10" data-testid="fiche-territoire-fermer"><X className="h-5 w-5" /></button>
      </div>
      <TypesDispositifs types={territoire.types} testid="fiche-territoire-types" />
      {publique ? null : !detail ? (
        <p className="mt-6 flex items-center gap-2 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" />Chargement…</p>
      ) : (
        <div className="mt-5 space-y-5 text-sm">
          <Bloc titre="Contrats en cours" n={detail.contrats.length} testid="fiche-territoire-contrats">
            {detail.contrats.map((c) => (
              <div key={c.souscription_id} className="rounded-lg bg-white/5 px-3 py-2">
                <p className="font-medium">{c.raison_sociale || '—'}</p>
                <p className="text-xs text-slate-400"><span className="mr-1 inline-block h-1.5 w-3 rounded-full" style={{ background: couleurType(c.dispositif) }} />{c.dispositif} · {c.reference} · {fr(c.debut)} → {fr(c.fin)}</p>
              </div>
            ))}
          </Bloc>
          <Bloc titre="Chantiers en cours (RCDS nominative)" n={detail.chantiers.length} testid="fiche-territoire-chantiers">
            {detail.chantiers.map((c) => (
              <div key={c.souscription_id} className="rounded-lg bg-amber-500/10 px-3 py-2">
                <p className="font-medium">{c.operation || c.raison_sociale || '—'}</p>
                <p className="text-xs text-slate-400">{[c.ville, c.type_operation?.replace(/_/g, ' ')].filter(Boolean).join(' · ')} · jusqu'au {fr(c.fin)}</p>
              </div>
            ))}
          </Bloc>
          <Bloc titre="Dommages déclarés" n={detail.dommages.length} testid="fiche-territoire-dommages">
            {detail.dommages.map((d) => (
              <div key={d.id} className={`rounded-lg px-3 py-2 ${d.etat === 'termine' ? 'bg-indigo-500/10' : 'bg-red-500/10'}`}>
                <p className="font-medium">{d.description || '—'}</p>
                <p className="text-xs text-slate-400">{fr(d.date)} · {d.etat === 'termine' ? 'Terminé' : 'En cours'}{d.raison_sociale ? ` · ${d.raison_sociale}` : ''}{d.evaluation ? ` · ${Number(d.evaluation).toLocaleString('fr-FR')} €` : ''}</p>
              </div>
            ))}
          </Bloc>
        </div>
      )}
    </div>
  );
}
